import { ConsentFilterCover } from "../consent-filter/ConsentFilterCover.js";
import { ConsentListItem } from "./ConsentListItem.js";
import { NoConsentsFoundCard } from "./NoConsentsFoundCard.js";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FilterSelection, IconFilter, Stack, useNavBar } from "@myphx-engineering/phx-design-system";
import { useDisclosure } from "@myphx-engineering/phx-design-system/hooks";
import { jsx, jsxs } from "react/jsx-runtime";

//#region src/client/components/consent-list/ConsentListWithFilter.tsx
const FILTER_STORAGE_KEY = "consentFilters";
const ConsentListWithFilter = ({ consents, onConsentClick }) => {
	const { setBackOverrideState } = useNavBar();
	const { t } = useTranslation("cons");
	const loadSavedFilters = () => {
		const saved = sessionStorage.getItem(FILTER_STORAGE_KEY);
		if (saved) try {
			const parsed = JSON.parse(saved);
			return {
				filters: parsed.filters || {
					status: "ACTIVE",
					type: "ALL"
				},
				filterText: parsed.filterText || t("consentStatus.accepted"),
				activeFilterCount: parsed.activeFilterCount ?? 0
			};
		} catch {
			return {
				filters: {
					status: "ACTIVE",
					type: "ALL"
				},
				filterText: t("consentStatus.accepted"),
				activeFilterCount: 0
			};
		}
		return {
			filters: {
				status: "ACTIVE",
				type: "ALL"
			},
			filterText: t("consentStatus.accepted"),
			activeFilterCount: 0
		};
	};
	const savedState = loadSavedFilters();
	const [filterOpen, { open: openFilter, close: closeFilter }] = useDisclosure(false);
	const [activeFilterCount, setActiveFilterCount] = useState(savedState.activeFilterCount);
	const [filterText, setFilterText] = useState(savedState.filterText);
	const [filters, setFilters] = useState(savedState.filters);
	const [filteredConsents, setFilteredConsents] = useState(consents);
	const sortAndGroupConsents = useCallback((consents$1) => {
		if (filters.status !== "ALL") return consents$1.sort((a, b) => new Date(b.activityDate).getTime() - new Date(a.activityDate).getTime());
		const statusOrder = [
			"ACTIVE",
			"REVOKED",
			"DECLINED"
		];
		const grouped = [];
		statusOrder.forEach((status) => {
			const statusConsents = consents$1.filter((consent) => consent.status === status).sort((a, b) => new Date(b.activityDate).getTime() - new Date(a.activityDate).getTime());
			grouped.push(...statusConsents);
		});
		return grouped;
	}, [filters.status]);
	useEffect(() => {
		if (!consents) return;
		let filtered = [...consents];
		if (filters.status !== "ALL") filtered = filtered.filter((consent) => consent.status === filters.status);
		if (filters.type !== "ALL") {
			if (filters.type === "REQUIRED") filtered = filtered.filter((consent) => consent.document.required);
			else if (filters.type === "OPTIONAL") filtered = filtered.filter((consent) => !consent.document.required);
		}
		setFilteredConsents(sortAndGroupConsents(filtered));
	}, [
		consents,
		filters,
		sortAndGroupConsents
	]);
	const handleFiltersApplied = useCallback((filtered, newFilters, newFilterText, filterCount) => {
		setFilteredConsents(filtered);
		setFilters(newFilters);
		setFilterText(newFilterText);
		setActiveFilterCount(filterCount);
		sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify({
			filters: newFilters,
			filterText: newFilterText,
			activeFilterCount: filterCount
		}));
	}, []);
	const handleConsentClick = (consentId) => {
		onConsentClick(consentId);
	};
	const handleBack = useCallback(() => {
		closeFilter();
	}, [closeFilter]);
	useEffect(() => {
		if (filterOpen) setBackOverrideState({ overrideFn: handleBack });
		else setBackOverrideState({ overrideFn: null });
		return () => setBackOverrideState({ overrideFn: null });
	}, [
		handleBack,
		filterOpen,
		setBackOverrideState
	]);
	return /* @__PURE__ */ jsxs(Stack, {
		gap: "lg",
		flex: "1",
		justify: "space-between",
		children: [
			/* @__PURE__ */ jsx(FilterSelection, {
				input: filterText,
				EndIcon: IconFilter,
				onEndIconClick: openFilter,
				endIconHasNotification: activeFilterCount > 0
			}),
			filteredConsents.length === 0 ? /* @__PURE__ */ jsx(NoConsentsFoundCard, {}) : /* @__PURE__ */ jsx(Stack, {
				gap: "sm",
				children: filteredConsents.map((consent) => /* @__PURE__ */ jsx(ConsentListItem, {
					id: consent.id,
					status: consent.status,
					document: consent.document,
					handleClick: handleConsentClick
				}, consent.id))
			}),
			/* @__PURE__ */ jsx(ConsentFilterCover, {
				isOpen: filterOpen,
				onClose: closeFilter,
				consents,
				currentFilters: filters,
				onFiltersApplied: handleFiltersApplied
			})
		]
	});
};

//#endregion
export { ConsentListWithFilter };